import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, IconButton, Tooltip, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import StarIcon from '@mui/icons-material/Star';
import apiService from '../services/api';
import { hasMeaningfulDefects } from '../utils/defects';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
  '&.annotated': {
    border: '3px solid #4caf50',
  },
}));

const MediaFrame = styled(Box)({
  position: 'relative',
  width: '100%',
  paddingTop: '75%', // 4:3 aspect ratio
  backgroundColor: '#f2f3f5',
});

const MediaImg = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const MediaSkeleton = styled(Skeleton)({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  transform: 'none',
});

const EmptyMediaState = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.grey[100],
}));

const ImageContainer = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
});

const StatusRibbon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: -25,
  backgroundColor: '#4caf50',
  color: 'white',
  padding: '4px 30px',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  transform: 'rotate(45deg)',
  transformOrigin: 'center',
  zIndex: 2,
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 100%)',
    zIndex: -1,
  }
}));

const StatusIcon = styled(CheckCircleIcon)({
  position: 'absolute',
  top: 8,
  left: 8,
  color: '#4caf50',
  backgroundColor: 'white',
  borderRadius: '50%',
  zIndex: 2,
  fontSize: '1.5rem',
});

const CoverRibbon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  left: 8,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 10px',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  borderRadius: 6,
  zIndex: 2,
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
}));
const KeyFindingRibbon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 36,
  left: 8,
  backgroundColor: '#ff9800',
  color: 'white',
  padding: '4px 10px',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  borderRadius: 6,
  zIndex: 2,
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
}));

const getDisplayFilename = (image) => {
  const raw =
    image?.filename ||
    image?.name ||
    image?.originalImage?.filename ||
    image?.originalImage?.name ||
    image?.url ||
    image?.src ||
    image?.elevation ||
    '';
  const base = String(raw).split(/[\\/]/).pop() || String(raw);
  return base.replace(/\.[^.]+$/, '') || image?.elevation || '';
};

const UploadStatePill = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 8,
  left: 8,
  backgroundColor: 'rgba(33, 33, 33, 0.82)',
  color: 'white',
  padding: '4px 10px',
  fontSize: '0.7rem',
  fontWeight: 700,
  borderRadius: 999,
  zIndex: 2,
  letterSpacing: 0.4,
}));
const ActionBar = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  display: 'flex',
  gap: theme.spacing(1),
  zIndex: 3,
}));

const ImagePreview = ({ image, onDelete, isDraggable = false, onDragStart, onDragOver, onDrop, onDragEnd, buildingId = null, onSetCover = null, isCover = false, elevationName = null, imageType = 'drone', isKeyFinding = false, onToggleKeyFinding = null, onClick = null }) => {
  const [hasAnnotations, setHasAnnotations] = useState(false);
  const [hasDefects, setHasDefects] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [thumbUrl, setThumbUrl] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  
  // API service is imported as singleton

  // Generate a unique key for each image based on its properties
  const getImageKey = (imageData) => {
    if (!imageData) return null;
    const idPart =
      imageData.id ||
      imageData.file_url ||
      imageData.url ||
      imageData.src ||
      imageData.filename ||
      (imageData.originalImage && (imageData.originalImage.id || imageData.originalImage.url || imageData.originalImage.src)) ||
      'unknown';
    const bPart = buildingId || imageData.building_id || '';
    return `annotations_${String(idPart)}_${String(bPart)}`;
  };

  // Load image URL from backend if needed
  useEffect(() => {
    const loadImageUrl = async () => {
      setImageLoaded(false);
      setImageLoadFailed(false);
      if (image?.uploadStatus === 'local' || image?.localPreview) {
        setImageUrl(image.src || '');
        setThumbUrl(image.src || '');
        return;
      }
      if (image.id && image.src && typeof image.src === 'string' && !image.src.startsWith('data:')) {
        try {
          const url = apiService.getImageUrl(image.id);
          setImageUrl(url);
          setThumbUrl(apiService.getImageThumbUrl(image.id, 320, 'webp'));
        } catch (error) {
          console.error('Error loading image URL:', error);
          // Fallback to original src
          setImageUrl(image.src);
          setThumbUrl(image.src);
        }
      } else if (image.id && (!image.src || typeof image.src !== 'string')) {
        // If src is not a string or doesn't exist, get URL from backend
        try {
          const url = apiService.getImageUrl(image.id);
          setImageUrl(url);
          setThumbUrl(apiService.getImageThumbUrl(image.id, 320, 'webp'));
        } catch (error) {
          console.error('Error loading image URL:', error);
          setImageUrl('');
          setThumbUrl('');
        }
      } else {
        setImageUrl(image.src || '');
        setThumbUrl(image.src || '');
      }
    };

    loadImageUrl();
  }, [image.id, image.src, image?.uploadStatus, image?.localPreview]);

  // Check if image has annotations from backend
  useEffect(() => {
    const checkAnnotations = async () => {
      if (image?.uploadStatus === 'local' || image?.localPreview) {
        const imageKey = getImageKey(image);
        try {
          const savedAnnotations = imageKey ? localStorage.getItem(imageKey) : null;
          const savedData = imageKey ? localStorage.getItem(`${imageKey}_data`) : null;
          const hasDrawingAnnotations = Boolean(savedAnnotations && JSON.parse(savedAnnotations).length > 0);
          const hasLocalDefects = Boolean(savedData && hasMeaningfulDefects(JSON.parse(savedData)?.defects));
          const hasLocalRecommendation = Boolean(savedData && JSON.parse(savedData)?.recommendation && JSON.parse(savedData).recommendation.trim() !== '');
          setHasAnnotations(hasDrawingAnnotations || hasLocalDefects || hasLocalRecommendation);
          setHasDefects(hasLocalDefects);
        } catch {
          setHasAnnotations(false);
          setHasDefects(false);
        }
        return;
      }

      const hasImageDefects =
        hasMeaningfulDefects(image?.defects) ||
        hasMeaningfulDefects(image?.originalImage?.defects);

      const checkLocalState = () => {
        const imageKey = getImageKey(image);
        let hasDrawingAnnotations = false;
        let hasLocalDefects = false;
        let hasLocalRecommendation = false;

        if (imageKey) {
          try {
            const savedAnnotations = localStorage.getItem(imageKey);
            const savedData = localStorage.getItem(`${imageKey}_data`);

            hasDrawingAnnotations = Boolean(savedAnnotations && JSON.parse(savedAnnotations).length > 0);
            if (savedData) {
              const data = JSON.parse(savedData);
              hasLocalDefects = hasMeaningfulDefects(data.defects);
              hasLocalRecommendation = Boolean(data.recommendation && data.recommendation.trim() !== '');
            }
          } catch (error) {
            console.error('Error checking local annotations:', error);
          }
        }

        return {
          hasLocalAnnotations: hasDrawingAnnotations || hasLocalDefects || hasLocalRecommendation,
          hasLocalDefects,
        };
      };

      if (image.id) {
        try {
          // Check backend for defect annotations
          const defects = await apiService.getImageDefects(image.id);
          const hasBackendDefects = hasMeaningfulDefects(defects?.defects);
          const { hasLocalAnnotations, hasLocalDefects } = checkLocalState();

          setHasAnnotations(hasBackendDefects || hasLocalAnnotations || hasImageDefects);
          setHasDefects(hasBackendDefects || hasLocalDefects || hasImageDefects);
        } catch (error) {
          console.error('Error checking backend annotations:', error);
          // Fallback to localStorage check only
          const { hasLocalAnnotations, hasLocalDefects } = checkLocalState();
          setHasAnnotations(hasLocalAnnotations || hasImageDefects);
          setHasDefects(hasLocalDefects || hasImageDefects);
        }
      } else {
        const { hasLocalAnnotations, hasLocalDefects } = checkLocalState();
        setHasAnnotations(hasLocalAnnotations || hasImageDefects);
        setHasDefects(hasLocalDefects || hasImageDefects);
      }
    };

    checkAnnotations();

    const handleUpdated = (evt) => {
      const imageId = evt?.detail?.imageId;
      if (!imageId) return;
      if (String(imageId) !== String(image.id)) return;
      checkAnnotations();
    };

    window.addEventListener('pfi-annotations-updated', handleUpdated);

    return () => {
      window.removeEventListener('pfi-annotations-updated', handleUpdated);
    };
  }, [image.id, buildingId, image?.uploadStatus, image?.localPreview]);

  const uploadStatus = image?.uploadStatus || (image?.localPreview ? 'local' : 'server');
  const uploadLabel =
    uploadStatus === 'local' ? 'LOCAL' :
    uploadStatus === 'uploading' ? 'UPLOADING' :
    uploadStatus === 'failed' ? 'FAILED' :
    'SERVER';
  const displayTitle = getDisplayFilename(image);
  const displaySrc = thumbUrl || imageUrl;

  return (
    <StyledCard
      className={hasAnnotations ? 'annotated' : ''}
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onClick={() => onClick && onClick(image)}
    >
      <ImageContainer>
        <MediaFrame>
          {!imageLoaded && !imageLoadFailed && (
            <MediaSkeleton variant="rectangular" animation="wave" />
          )}
          {displaySrc && !imageLoadFailed ? (
            <MediaImg
              key={`${image.id || image.src || imageUrl}|${displaySrc}`}
              src={displaySrc}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              alt={image.elevation || ''}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                if (thumbUrl && imageUrl && e?.currentTarget?.src !== imageUrl) {
                  setThumbUrl('');
                  e.currentTarget.src = imageUrl;
                  return;
                }
                setImageLoaded(true);
                setImageLoadFailed(true);
              }}
              style={{ opacity: imageLoaded ? 1 : 0 }}
            />
          ) : (
            <EmptyMediaState>
              <ImageIcon fontSize="large" />
            </EmptyMediaState>
          )}
        </MediaFrame>
        <ActionBar>
          {onSetCover && (
            <Tooltip title={isCover ? 'Selected as Elevation Image' : 'Set as Elevation Image'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onSetCover(image); }}
                sx={{ 
                  backgroundColor: isCover ? 'primary.light' : 'rgba(255,255,255,0.9)',
                  border: isCover ? '2px solid' : '1px solid',
                  borderColor: isCover ? 'primary.main' : 'divider',
                  color: isCover ? 'primary.contrastText' : 'inherit'
                }}
              >
                <ImageIcon fontSize="small" sx={{ color: isCover ? 'primary.main' : 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          )}
          {onToggleKeyFinding && hasDefects && (
            <Tooltip title={isKeyFinding ? 'Marked as Key Findings' : 'Mark as Key Findings'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onToggleKeyFinding(image); }}
                sx={{
                  backgroundColor: isKeyFinding ? 'orange' : 'rgba(255,255,255,0.9)',
                  border: isKeyFinding ? '2px solid orange' : '1px solid',
                  borderColor: isKeyFinding ? 'orange' : 'divider',
                  color: isKeyFinding ? 'white' : 'inherit',
                }}
              >
                <StarIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </ActionBar>
        {hasAnnotations && (
          <>
            <StatusIcon />
            <StatusRibbon>
              ANNOTATED
            </StatusRibbon>
          </>
        )}
        {isCover && (
          <CoverRibbon>
            ELEVATION IMAGE
          </CoverRibbon>
        )}
        {isKeyFinding && hasDefects && (
          <KeyFindingRibbon>
            KEY FINDINGS
          </KeyFindingRibbon>
        )}
        <UploadStatePill sx={{
          backgroundColor:
            uploadStatus === 'local' ? 'rgba(158, 158, 158, 0.88)' :
            uploadStatus === 'uploading' ? 'rgba(25, 118, 210, 0.88)' :
            uploadStatus === 'failed' ? 'rgba(211, 47, 47, 0.9)' :
            'rgba(46, 125, 50, 0.88)'
        }}>
          {uploadLabel}
        </UploadStatePill>

      </ImageContainer>
      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: '16px !important' }}>
        <Box>
          <Typography variant="subtitle1" component="div" fontWeight="bold">
            {displayTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {image.floor}
          </Typography>
          {isCover && (
            <Typography variant="caption" color="primary">
              Elevation Image
            </Typography>
          )}
        </Box>
        {onDelete && (
          <IconButton 
            size="small" 
            color="error" 
            onClick={(e) => { e.stopPropagation(); onDelete(image); }} 
            aria-label="Delete image"
            sx={{ 
              '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' }
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default ImagePreview;
